import React from 'react';
import { Container, Title, Text, Stack, Space, Tabs, Button, Group, Badge } from '@mantine/core';
import { IconReceipt, IconHistory, IconDownload, IconBuildingHospital } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import BillingFilters from '../components/BillingFilters';
import BillingOrdersTable from '../components/BillingOrdersTable';
import BillingActionButtons from '../components/BillingActionButtons';
import ClinicSeatBillingTab from '../components/ClinicSeatBillingTab';
import { useBillingReport } from '../../../global/entities/TestKitOrder/queries/useBillingReport';
import { useBillingBatchActions, useRecentBillingBatches } from '../../../global/hooks/useBillingBatch';
import { IBillingReportFilter, ICreateBillingBatchRequest } from '../../../global/entities/TestKitOrder/BillingTypes';

/**
 * BillingManagementPage - Admin page for managing test kit order billing
 * Part of BL-002 Admin Billing Reports Feature - Step 43: Updated for batch workflow
 * Note: Admin access is enforced at the route level
 */
const BillingManagementPage: React.FC = () => {
    const { t } = useTranslation();
    const [selectedOrders, setSelectedOrders] = React.useState<string[]>([]);
    const [activeTab, setActiveTab] = React.useState<string | null>('orders');
    
    // Default filter to load all orders initially
    const [currentFilters, setCurrentFilters] = React.useState<IBillingReportFilter>({
        statusFilter: 2, // 2=All orders
        pageNumber: 1,
        pageSize: 25,
        sortBy: 'CreatedDate',
        sortDirection: 'DESC',
    });

    // Use hooks for data fetching and batch operations
    const { billingData, isLoading, error, refetch } = useBillingReport(currentFilters);
    const { createBatchAndExportCSV, exportBatchCSV, loading: batchLoading, error: batchError, clearError } = useBillingBatchActions();
    const { batches: recentBatches, loading: batchesLoading, refresh: refreshBatches } = useRecentBillingBatches(5);

    const handleFiltersChange = (filters: IBillingReportFilter) => {
        console.log('Applying filters:', filters);
        setCurrentFilters(filters);
    };

    const handleOrdersSelectionChange = (orderIds: string[]) => {
        setSelectedOrders(orderIds);
        console.log('Selected orders:', orderIds);
    };

    const handleBillAndExport = async (orderIds: string[]) => {
        try {
            clearError();
            const request: ICreateBillingBatchRequest = {
                orderIDs: orderIds.map(id => parseInt(id, 10)),
                description: t('billing.batch.description', { count: orderIds.length, date: new Date().toLocaleDateString() })
            };
            
            const success = await createBatchAndExportCSV(request);
            if (success) {
                setSelectedOrders([]);
                // Refresh both orders and batches data
                await refetch();
                refreshBatches();
                console.log('Successfully created batch and exported CSV');
            }
        } catch (error) {
            console.error('Failed to create batch and export:', error);
        }
    };

    const handleExportBatch = async (batchId: number, csvFileName?: string) => {
        try {
            clearError();
            await exportBatchCSV(batchId, csvFileName);
            console.log(`Successfully re-exported batch ${batchId}`);
        } catch (error) {
            console.error(`Failed to export batch ${batchId}:`, error);
        }
    };

    const handleRefresh = () => {
        console.log('Refreshing billing data...');
        setSelectedOrders([]);
        refetch();
    };

    return (
        <Container size="xl" p="md">
            <Stack gap="lg">
                <div>
                    <Title order={2} mb="xs">
                        {t('billing.management.title')}
                    </Title>
                    <Text c="dimmed" size="sm">
                        {t('billing.management.description')}
                    </Text>
                </div>

                <Space h="sm" />
                
                <Tabs value={activeTab} onChange={setActiveTab}>
                    <Tabs.List>
                        <Tabs.Tab value="orders" leftSection={<IconReceipt size={16} />}>
                            {t('billing.tabs.orders')}
                        </Tabs.Tab>
                        <Tabs.Tab value="batches" leftSection={<IconHistory size={16} />}>
                            {t('billing.tabs.batches')}
                            {recentBatches?.hasResults && (
                                <Badge size="xs" ml="xs" color="blue">
                                    {recentBatches.batches.length}
                                </Badge>
                            )}
                        </Tabs.Tab>
                        <Tabs.Tab value="clinicSeats" leftSection={<IconBuildingHospital size={16} />}>
                            {t('billing.tabs.clinicSeats')}
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="orders" pt="lg">
                        <Stack gap="md">
                            <BillingFilters 
                                onFiltersChange={handleFiltersChange} 
                                isLoading={isLoading}
                            />
                            
                            {(error || batchError) && (
                                <Text c="red" size="sm">
                                    ⚠️ {error?.message || batchError || t('billing.errors.loadFailed')}
                                </Text>
                            )}
                            
                            <BillingActionButtons
                                selectedOrders={selectedOrders}
                                onBillAndExport={handleBillAndExport}
                                onRefresh={handleRefresh}
                                isLoading={isLoading || batchLoading}
                            />
                            
                            <BillingOrdersTable 
                                onOrdersSelectionChange={handleOrdersSelectionChange}
                                isLoading={isLoading}
                                billingData={billingData}
                            />
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="batches" pt="lg">
                        <Stack gap="md">
                            <Group justify="space-between">
                                <Title order={4}>{t('billing.batches.title')}</Title>
                                <Button 
                                    variant="light" 
                                    size="sm" 
                                    onClick={refreshBatches}
                                    loading={batchesLoading}
                                >
                                    {t('billing.actions.refresh')}
                                </Button>
                            </Group>
                            
                            {batchesLoading ? (
                                <Text c="dimmed">{t('billing.batches.loading')}</Text>
                            ) : recentBatches?.hasResults ? (
                                <Stack gap="xs">
                                    {recentBatches.batches.map((batch: any) => (
                                        <Group key={batch.id} justify="space-between" p="sm" style={{ border: '1px solid #e9ecef', borderRadius: '8px' }}>
                                            <div>
                                                <Text fw={500}>{batch.batchNumber}</Text>
                                                <Text size="sm" c="dimmed">
                                                    {t('billing.batches.summary', { 
                                                        count: batch.orderCount, 
                                                        amount: batch.formattedTotalAmount, 
                                                        date: new Date(batch.createdDateTime).toLocaleDateString() 
                                                    })}
                                                </Text>
                                            </div>
                                            <Button
                                                size="xs"
                                                variant="light"
                                                leftSection={<IconDownload size={14} />}
                                                onClick={() => handleExportBatch(batch.id, batch.csvFileName)}
                                                loading={batchLoading}
                                            >
                                                {t('billing.actions.redownload')}
                                            </Button>
                                        </Group>
                                    ))}
                                </Stack>
                            ) : (
                                <Text c="dimmed" ta="center" py="xl">
                                    {t('billing.batches.empty')}
                                </Text>
                            )}
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="clinicSeats" pt="lg">
                        <ClinicSeatBillingTab />
                    </Tabs.Panel>
                </Tabs>
            </Stack>
        </Container>
    );
};

export default BillingManagementPage; 